import React, { useState } from "react";
import { Star, Loader2, Check, PenLine } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const inputCls =
  "w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60";

// Devotees share their own experience — lands in the admin as pending for approval.
export default function TestimonialForm() {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || text.trim().length < 20) {
      toast.error(t("share_invalid"));
      return;
    }
    setSending(true);
    try {
      await api.post("/testimonials/submit", {
        name: name.trim(),
        location: location.trim(),
        text_en: text.trim(),
        text_bn: lang === "bn" ? text.trim() : "",
        text_hi: lang === "hi" ? text.trim() : "",
        rating,
      });
      setDone(true);
      toast.success(t("share_success"));
    } catch {
      toast.error(t("share_failed"));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="glass rounded-[1.6rem] p-10 text-center" data-testid="share-done">
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-tara-gold to-tara-vermilion">
          <Check className="h-7 w-7 text-white" />
        </span>
        <p className="font-display text-2xl text-tara-ink mb-2">{t("share_success")}</p>
        <p className="text-sm text-tara-muted">{t("share_pending_note")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-[1.6rem] p-7 space-y-4" data-testid="share-experience-form">
      <div>
        <span className="badge-gold inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] font-serif uppercase tracking-[0.24em] mb-3">
          <PenLine className="h-3 w-3" /> {t("share_eyebrow")}
        </span>
        <h3 className="font-serif text-2xl font-bold text-tara-ink mb-1.5">{t("share_title")}</h3>
        <p className="text-sm text-tara-body leading-relaxed">{t("share_sub")}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("field_name")} className={inputCls} data-testid="share-name-input" />
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t("field_location")} className={inputCls} data-testid="share-location-input" />
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={t("share_placeholder")} className={inputCls} data-testid="share-text-input" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-1.5" data-testid="share-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className="transition-transform hover:scale-110"
              data-testid={`share-star-${n}`}
              aria-label={`${n} stars`}
            >
              <Star className={`h-5 w-5 ${n <= rating ? "text-tara-gold fill-current" : "text-tara-muted/40"}`} />
            </button>
          ))}
        </div>
        <button type="submit" disabled={sending} className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold" data-testid="share-submit-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} {t("share_cta")}
        </button>
      </div>
      <p className="text-[11px] text-tara-muted leading-relaxed">{t("share_privacy")}</p>
    </form>
  );
}
